import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

export interface SearchSelectOption<TId extends string | number = number> {
  id: TId;
  label: string;
  subLabel?: string;
  displayLabel?: string;
}

@Component({
  selector: 'app-search-select',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './search-select.component.html',
  styleUrls: ['./search-select.component.scss'],
})
export class SearchSelectComponent<TId extends string | number = number> implements OnChanges, OnDestroy {
  @Input() options: SearchSelectOption<TId>[] = [];
  @Input() placeholder = 'Search...';
  @Input() disabled = false;
  @Input() invalid = false;

  @Input() selectedId: TId | null = null;
  @Output() selectedIdChange = new EventEmitter<TId | null>();

  isOpen = false;
  query = '';
  filtered: SearchSelectOption<TId>[] = [];

  private readonly query$ = new Subject<string>();
  private readonly instanceId = `ss-${Math.random().toString(36).slice(2)}`;
  private readonly subs = new Subscription();

  private static readonly openState$ = new Subject<string>();

  constructor(private readonly elRef: ElementRef<HTMLElement>) {
    this.subs.add(
      this.query$
        .pipe(debounceTime(200), distinctUntilChanged())
        .subscribe((q) => this.applyFilter(q)),
    );

    this.subs.add(
      SearchSelectComponent.openState$.subscribe((openedInstanceId) => {
        if (openedInstanceId !== this.instanceId && this.isOpen) {
          this.close();
        }
      }),
    );
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.applyFilter(this.query);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /** Close when clicking outside */
  @HostListener('document:click', ['$event'])
  onDocClick(evt: MouseEvent): void {
    if (!this.isOpen) return;

    const host = this.elRef.nativeElement;
    const target = evt.target as Node | null;

    if (target && !host.contains(target)) {
      this.close();
    }
  }

  toggleOpen(evt: Event): void {
    evt.stopPropagation();

    if (this.disabled) return;

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    if (this.disabled) return;

    SearchSelectComponent.openState$.next(this.instanceId);
    this.isOpen = true;
    this.applyFilter(this.query);
  }

  close(): void {
    this.isOpen = false;
    this.query = '';
    this.applyFilter('');
  }

  onQueryInput(value: string): void {
    this.query = value;
    this.query$.next(value);
  }

  selectOnMouseDown(evt: MouseEvent, opt: SearchSelectOption<TId>): void {
    evt.preventDefault();
    evt.stopPropagation();

    this.selectedId = opt.id;
    this.selectedIdChange.emit(opt.id);
    this.close();
  }

  clearSelection(evt: MouseEvent): void {
    evt.preventDefault();
    evt.stopPropagation();

    this.selectedId = null;
    this.selectedIdChange.emit(null);
    this.close();
  }

  onCloseClick(evt: MouseEvent): void {
    evt.preventDefault();
    evt.stopPropagation();
    this.close();
  }

  swallow(evt: Event): void {
    evt.stopPropagation();
  }

  get selectedLabel(): string {
    const hit = this.options.find((o) => o.id === this.selectedId);
    return hit?.label ?? '';
  }

  get selectedSubLabel(): string {
    const hit = this.options.find((o) => o.id === this.selectedId);
    return hit?.subLabel ?? '';
  }

  private applyFilter(q: string): void {
    const term = (q ?? '').trim().toLowerCase();

    const base = term
      ? this.options.filter((o) => {
          const label = (o.label ?? '').toLowerCase();
          const subLabel = (o.subLabel ?? '').toLowerCase();
          return label.includes(term) || subLabel.includes(term);
        })
      : this.options;

    this.filtered = base.slice(0, 25);
  }
}